import { Formula, FormulaRule, Vehicle } from '@otw/models'
import { OLSTrainedModel } from '@otw/models/ols'
import { useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useInventory } from '../inventory'
import { calculatePrice } from './useFormula'
import { useOLSPrediction } from '@/hooks/ols/useOLSPrediction'

export interface OptimizationChange {
  type: 'maxPrice' | 'minPrice' | 'operationValue'
  ruleId?: string // Only for operationValue changes
  currentValue: number
  suggestedValue: number
  description: string
}

export interface OptimizationResult {
  changes: OptimizationChange[]
  currentAvgError: number
  projectedAvgError: number
  improvement: number
  totalChanges: number
}

export interface FormulaStat {
  formula: Formula
  sold: number
  soldElsewhere: number
  totalSold: number
  avgErrorPercent: number // Mean Absolute Error (MAE) as percentage (all observations)
  avgErrorHigh: number // Average error when actual price is higher than predicted
  avgErrorLow: number // Average error when actual price is lower than predicted
  countAbovePrediction: number // Count of sales above prediction
  countBelowPrediction: number // Count of sales below prediction
  totalAbovePrediction: number // Total amount above prediction ($)
  totalBelowPrediction: number // Total amount below prediction ($)
  rSquared: number
  // Holdout MAPE — calculated on the last 20% of time-ordered observations.
  // Use this for fair model comparison (both formula and automated use same method).
  holdoutMape: number | null // MAPE on holdout set (null if <3 holdout observations)
  automatedMape: number | null // MAPE of OLS model on same holdout set
  holdoutVehicleIds: string[] // IDs of vehicles in the holdout set
  matchingVehicles: number
  vehicleDescription: string
  sampleSize: number
  optimizationResult: OptimizationResult | null // Suggested optimizations
  hasImprovement?: boolean
}

// ---------------------------------------------------------------------------
// Generic predict function abstraction
// Both formula and WLS (client-side or API) implement this interface.
// ---------------------------------------------------------------------------

export interface PredictInput {
  year: number
  mileage: number
  make?: string
  model?: string
  type?: string
  size?: string
  fuel?: string
  color?: string
  dateAdded?: string
}

/** Batch async predict function — all pricing models implement this signature. */
export type PricingPredictFn = (inputs: PredictInput[]) => Promise<(number | null)[]>

/** Build a predict function from a formula + rules (sync, wrapped in async). */
export const createFormulaPredictFn = (
  formula: Formula,
  rules: FormulaRule[],
  priceType: 'retail' | 'wholesale',
): PricingPredictFn =>
  async (inputs) =>
    inputs.map(({ year, mileage }) => {
      const { price } = calculatePrice({ input: { year, mileage }, formula, rules })
      if (price <= 0) return null
      return priceType === 'wholesale'
        ? price * (1 - (formula.wholesaleDiscount || 0) / 100)
        : price
    })

/**
 * Client-side OLS prediction using model coefficients directly.
 * Mirrors the predictWithWLS function in OLSRegression.ts on the API.
 */
function predictFromOLSModel(
  model: OLSTrainedModel,
  features: { year: number; mileage: number; make: string; model: string; type: string; size: string; fuel: string; color: string; dateAdded?: string }
): number | null {
  const { coefficients, minBound, maxBound } = model
  const coef = coefficients as Record<string, any>

  // Calculate age from year and dateAdded (mirrors server-side logic)
  const currentYear = features.dateAdded
    ? new Date(features.dateAdded).getFullYear()
    : new Date().getFullYear()
  const age = currentYear - features.year

  // Age bucket: 0=age_0_2, 1=age_2_4, 2=age_4_6, 3=older (no coefficient)
  let ageBucket = 3
  if (age >= 0 && age < 2) ageBucket = 0
  else if (age >= 2 && age < 4) ageBucket = 1
  else if (age >= 4 && age < 6) ageBucket = 2

  // daysFromRef
  let daysFromRef = 0
  if (coef.referenceDate && features.dateAdded) {
    const refTime = new Date(coef.referenceDate).getTime()
    const inputTime = new Date(features.dateAdded).getTime()
    daysFromRef = (inputTime - refTime) / (1000 * 60 * 60 * 24)
  }

  let prediction = coef.intercept || 0
  prediction += (coef.mileage || 0) * (features.mileage / 1000)
  prediction += (coef.daysFromRef || 0) * daysFromRef

  // One-hot categorical features (matches on key name)
  for (const [key, value] of Object.entries(coef)) {
    if (typeof value !== 'number' || !key.includes('_')) continue

    if (key.startsWith('make_') && key === `make_${features.make}`) {
      prediction += value
    } else if (key.startsWith('model_') && key === `model_${features.model}`) {
      prediction += value
    } else if (key.startsWith('type_') && key === `type_${features.type}`) {
      prediction += value
    } else if (key.startsWith('size_') && key === `size_${features.size}`) {
      prediction += value
    } else if (key.startsWith('fuel_') && key === `fuel_${features.fuel}`) {
      prediction += value
    } else if (key.startsWith('color_') && key === `color_${features.color}`) {
      prediction += value
    } else if (key.startsWith('age_')) {
      const ageBucketNames = ['age_0_2', 'age_2_4', 'age_4_6']
      if (key === ageBucketNames[ageBucket]) {
        prediction += value
      }
    }
  }

  // Diesel interaction term
  const isDiesel = features.fuel === 'Diesel' ? 1 : 0
  if (coef.mileageDieselInteraction) {
    prediction += coef.mileageDieselInteraction * (features.mileage / 1000) * isDiesel
  }

  if (!Number.isFinite(prediction)) return null
  return Math.max(minBound, Math.min(maxBound, prediction))
}

/**
 * Build a predict function from an OLSTrainedModel using client-side coefficient application.
 * This explicitly "selects" a specific model version for comparison — no API call needed.
 * priceType is handled by which model is passed (sellModel vs buyInModel from OLSModelVersion).
 */
export const createOLSModelPredictFn = (
  olsModel: OLSTrainedModel,
): PricingPredictFn =>
  async (inputs) =>
    inputs.map((v) => {
      const price = predictFromOLSModel(olsModel, {
        year: v.year,
        mileage: v.mileage,
        make: v.make ?? '',
        model: v.model ?? '',
        type: v.type ?? '',
        size: v.size ?? '',
        fuel: v.fuel ?? 'Gas',
        color: v.color ?? 'White',
        dateAdded: v.dateAdded,
      })
      return price !== null && price > 0 ? price : null
    })

// Pre-calculated vehicle data for optimization
interface VehicleErrorData {
  year: number
  miles: number
  actualPrice: number
}

// Shared function to calculate average error - used by both main calculation and optimization
const calculateAverageError = (
  formula: Formula,
  rules: FormulaRule[],
  vehicleData: VehicleErrorData[],
  priceType: 'retail' | 'wholesale' = 'retail'
): number => {
  const errors: number[] = [];
  vehicleData.forEach(({ year, miles, actualPrice }) => {
    const { price: graybookRetailPrice } = calculatePrice({
      input: { year, mileage: miles },
      formula,
      rules,
    });

    if (graybookRetailPrice <= 0) return;

    const graybookPrice = priceType === 'retail'
      ? graybookRetailPrice
      : graybookRetailPrice * (1 - (formula.wholesaleDiscount || 0) / 100);

    const errorPercent = Math.abs(((actualPrice - graybookPrice) / graybookPrice) * 100);
    errors.push(errorPercent);
  });
  return errors.length > 0 ? errors.reduce((sum, val) => sum + val, 0) / errors.length : 0;
};

// Optimization algorithm to find minimal changes for reducing avgErrorPercent
const calculateOptimalChanges = (
  formula: Formula,
  rules: any[],
  vehicleData: VehicleErrorData[],
  currentAvgError: number,
  priceType: 'retail' | 'wholesale' = 'retail'
): OptimizationResult | null => {
  if (vehicleData.length === 0 || currentAvgError === 0) return null;
  const OPTIMIZATION_THRESHOLD = 5
  const OPTIMIZATION_IMPROVEMENT_THRESHOLD = 2

  if (currentAvgError <= OPTIMIZATION_THRESHOLD) return null;

  let bestChanges: OptimizationChange[] = [];
  let bestAvgError = currentAvgError;
  let bestFormula = formula;
  let bestRules = rules;

  const priceAdjustments = [-0.2, -0.15, -0.1, -0.05, 0.05, 0.1, 0.15, 0.2];
  const ruleAdjustments = [-0.3, -0.2, -0.1, -0.05, 0.05, 0.1, 0.2, 0.3];

  for (let iteration = 0; iteration < 3; iteration++) {
    let foundImprovement = false;

    for (const adjustment of priceAdjustments) {
      const newMaxPrice = Math.round(bestFormula.maxPrice * (1 + adjustment));
      if (newMaxPrice <= 0 || newMaxPrice > formula.maxPrice * 2.5 || newMaxPrice < formula.maxPrice * 0.5) continue;

      const testFormula = { ...bestFormula, maxPrice: newMaxPrice };
      const avgError = calculateAverageError(testFormula, bestRules, vehicleData, priceType);

      if (avgError < bestAvgError - 0.5) {
        bestAvgError = avgError;
        bestFormula = testFormula;

        const existingIndex = bestChanges.findIndex(c => c.type === 'maxPrice');
        const change: OptimizationChange = {
          type: 'maxPrice',
          currentValue: formula.maxPrice,
          suggestedValue: newMaxPrice,
          description: `Adjust max price from $${formula.maxPrice.toLocaleString()} to $${newMaxPrice.toLocaleString()}`
        };

        if (existingIndex >= 0) {
          bestChanges[existingIndex] = change;
        } else {
          bestChanges.push(change);
        }
        foundImprovement = true;
        break;
      }
    }

    if (foundImprovement) continue;

    if (bestFormula.minPrice) {
      for (const adjustment of priceAdjustments) {
        const newMinPrice = Math.round(bestFormula.minPrice * (1 + adjustment));
        if (newMinPrice <= 0 || newMinPrice >= bestFormula.maxPrice) continue;

        const testFormula = { ...bestFormula, minPrice: newMinPrice };
        const avgError = calculateAverageError(testFormula, bestRules, vehicleData, priceType);

        if (avgError < bestAvgError - 0.5) {
          bestAvgError = avgError;
          bestFormula = testFormula;

          const existingIndex = bestChanges.findIndex(c => c.type === 'minPrice');
          const change: OptimizationChange = {
            type: 'minPrice',
            currentValue: formula.minPrice!,
            suggestedValue: newMinPrice,
            description: `Adjust min price from $${formula.minPrice!.toLocaleString()} to $${newMinPrice.toLocaleString()}`
          };

          if (existingIndex >= 0) {
            bestChanges[existingIndex] = change;
          } else {
            bestChanges.push(change);
          }
          foundImprovement = true;
          break;
        }
      }
    }

    if (foundImprovement) continue;

    if (bestRules && bestRules.length > 0) {
      let bestRuleChange: { rule: any; newValue: number; error: number } | null = null;

      for (const rule of bestRules) {
        if (rule.status !== 'active' || !rule.operationValue) continue;

        for (const adjustment of ruleAdjustments) {
          const newOperationValue = rule.operationValue * (1 + adjustment);
          if (newOperationValue <= 0) continue;

          const testRules = bestRules.map(r =>
            r.id === rule.id ? { ...r, operationValue: newOperationValue } : r
          );

          const avgError = calculateAverageError(bestFormula, testRules, vehicleData, priceType);

          if (avgError < bestAvgError - 0.5 && (!bestRuleChange || avgError < bestRuleChange.error)) {
            bestRuleChange = { rule, newValue: newOperationValue, error: avgError };
          }
        }
      }

      if (bestRuleChange) {
        bestAvgError = bestRuleChange.error;
        bestRules = bestRules.map(r =>
          r.id === bestRuleChange!.rule.id ? { ...r, operationValue: bestRuleChange!.newValue } : r
        );

        const ruleType = bestRuleChange.rule.targetFactor === 'year' ? 'year' : 'mileage';
        const unit = bestRuleChange.rule.targetFactor === 'year' ? 'per year' : 'per 1000 miles';
        const lowerBound = bestRuleChange.rule.lowerBound !== undefined ? bestRuleChange.rule.lowerBound : 'start';
        const upperBound = bestRuleChange.rule.upperBound !== undefined ? bestRuleChange.rule.upperBound : 'end';
        const rangeText = bestRuleChange.rule.targetFactor === 'year' ?
          `${lowerBound}-${upperBound}` :
          `${lowerBound.toLocaleString()}-${upperBound !== 'end' ? upperBound.toLocaleString() : 'end'} miles`;

        const existingIndex = bestChanges.findIndex(c => c.type === 'operationValue' && c.ruleId === bestRuleChange!.rule.id);
        const change: OptimizationChange = {
          type: 'operationValue',
          ruleId: bestRuleChange.rule.id,
          currentValue: bestRuleChange.rule.operationValue,
          suggestedValue: bestRuleChange.newValue,
          description: `Adjust ${ruleType} depreciation for range ${rangeText} from $${bestRuleChange.rule.operationValue.toFixed(2)} to $${bestRuleChange.newValue.toFixed(2)} ${unit}`
        };

        if (existingIndex >= 0) {
          bestChanges[existingIndex] = change;
        } else {
          bestChanges.push(change);
        }
        foundImprovement = true;
      }
    }

    if (!foundImprovement) break;
  }

  const improvement = ((currentAvgError - bestAvgError) / currentAvgError) * 100;

  if (improvement < OPTIMIZATION_IMPROVEMENT_THRESHOLD) return null;

  return {
    changes: bestChanges,
    currentAvgError,
    projectedAvgError: bestAvgError,
    improvement,
    totalChanges: bestChanges.length
  };
};

const HOLDOUT_RATIO = 0.2

// Helper function to calculate a single formula's stats (for React Query)
const calculateSingleFormulaStat = async (
  formula: Formula,
  vehicles: Vehicle[],
  priceType: 'retail' | 'wholesale',
  primaryPredictFn: PricingPredictFn,
  comparisonPredictFn?: PricingPredictFn,
  excludedVehicleIds: string[] = [],
  dateRange?: { start: Date | null; end: Date | null },
): Promise<FormulaStat> => {
  // Filter vehicles for this formula
  const matchingVehicles = vehicles.filter(
    v =>
      v.make?.toLowerCase() === formula.vehicleMake?.toLowerCase() &&
      v.model?.toLowerCase() === formula.vehicleModel?.toLowerCase() &&
      v.type?.toLowerCase() === formula.vehicleType?.toLowerCase() &&
      v.size?.toLowerCase() === formula.vehicleSize?.toLowerCase() &&
      v.fuel?.toLowerCase() === formula.vehicleFuel?.toLowerCase(),
  )

  const filteredSales = matchingVehicles.filter(v => {
    if (v.id && excludedVehicleIds.includes(v.id)) return false
    if (!(v.status === 'Sold' || v.status === 'Sold elsewhere')) return false
    if (v.year == null || v.miles == null) return false
    if (!((v.optic_list_price ?? 0) > 0) || !((v.seller_asking_price ?? 0) > 0)) return false
    if (dateRange?.start || dateRange?.end) {
      const dateStr = v.sold_date || v.date_added
      if (dateStr) {
        const date = new Date(dateStr)
        if (dateRange.start && date < dateRange.start) return false
        if (dateRange.end && date > dateRange.end) return false
      }
    }
    return true
  })

  const sortedByProfit = priceType === 'retail'
    ? filteredSales
        .slice()
        .sort(
          (a, b) =>
            ((b.optic_list_price ?? 0) - (b.seller_asking_price ?? 0)) -
            ((a.optic_list_price ?? 0) - (a.seller_asking_price ?? 0)),
        )
    : filteredSales.slice()

  const historicalSales = sortedByProfit.slice(0, Math.ceil(sortedByProfit.length))

  const sold = historicalSales.filter(v => v.status === 'Sold').length
  const soldElsewhere = historicalSales.filter(v => v.status === 'Sold elsewhere').length
  const totalSold = sold + soldElsewhere

  const vehicleDescription = [
    formula.vehicleMake,
    formula.vehicleModel,
    formula.vehicleType,
    formula.vehicleSize,
    formula.vehicleFuel,
  ]
    .filter(Boolean)
    .join(' ')

  if (historicalSales.length === 0) {
    return {
      formula,
      holdoutVehicleIds: [],
      sold,
      soldElsewhere,
      totalSold,
      avgErrorPercent: 0,
      avgErrorHigh: 0,
      avgErrorLow: 0,
      countAbovePrediction: 0,
      countBelowPrediction: 0,
      totalAbovePrediction: 0,
      totalBelowPrediction: 0,
      rSquared: 0,
      holdoutMape: null,
      automatedMape: null,
      matchingVehicles: matchingVehicles.length,
      vehicleDescription,
      sampleSize: 0,
      optimizationResult: null,
    }
  }

  // -----------------------------------------------------------------------
  // Holdout split: sort by date_added ascending, take last 20% as holdout.
  // Both primary and comparison MAPE are evaluated on the SAME holdout set.
  // When a date range is active the caller has already scoped to a specific
  // window — applying a further 20% cut would exclude most of those
  // observations, so skip the split and evaluate on all filtered sales.
  // -----------------------------------------------------------------------
  const holdoutSales = (() => {
    if (dateRange?.start || dateRange?.end) return [] // signal: use all filteredSales below
    const sorted = filteredSales
      .filter(v => v.year != null && v.miles != null)
      .slice()
      .sort((a, b) => {
        const tA = a.date_added ? new Date(a.date_added).getTime() : 0
        const tB = b.date_added ? new Date(b.date_added).getTime() : 0
        return tA - tB
      })
    const holdoutSize = Math.ceil(sorted.length * HOLDOUT_RATIO)
    return sorted.slice(-holdoutSize)
  })()

  const vehicleErrorData: VehicleErrorData[] = historicalSales
    .map(v => {
      const actualPrice = priceType === 'retail' ? v.optic_list_price : v.seller_asking_price
      if (!v.year || !v.miles || !actualPrice) return null
      return { year: v.year, miles: v.miles, actualPrice }
    })
    .filter((v): v is VehicleErrorData => v !== null)

  // -----------------------------------------------------------------------
  // Primary model error stats on all historical sales
  // -----------------------------------------------------------------------
  const primaryInputs: PredictInput[] = vehicleErrorData.map(({ year, miles }) => ({
    year,
    mileage: miles,
    make: formula.vehicleMake,
    model: formula.vehicleModel,
    type: formula.vehicleType,
    size: formula.vehicleSize,
    fuel: formula.vehicleFuel,
  }))

  const primaryPredictions = await primaryPredictFn(primaryInputs)

  const errorPercentages: number[] = []
  const errorPercentagesHigh: number[] = []
  const errorPercentagesLow: number[] = []
  let countAbovePrediction = 0
  let countBelowPrediction = 0
  let totalAbovePrediction = 0
  let totalBelowPrediction = 0

  vehicleErrorData.forEach(({ actualPrice }, idx) => {
    const predictedPrice = primaryPredictions[idx]
    if (predictedPrice == null || predictedPrice <= 0) return

    const errorPercent = ((actualPrice - predictedPrice) / predictedPrice) * 100
    const priceDifference = actualPrice - predictedPrice

    errorPercentages.push(Math.abs(errorPercent))

    if (errorPercent > 0) {
      errorPercentagesHigh.push(errorPercent)
      countAbovePrediction++
      totalAbovePrediction += priceDifference
    } else if (errorPercent < 0) {
      errorPercentagesLow.push(Math.abs(errorPercent))
      countBelowPrediction++
      totalBelowPrediction += Math.abs(priceDifference)
    }
  })

  const avgErrorPercent =
    errorPercentages.length > 0
      ? errorPercentages.reduce((sum, val) => sum + val, 0) / errorPercentages.length
      : 0

  const avgErrorHigh =
    errorPercentagesHigh.length > 0
      ? errorPercentagesHigh.reduce((sum, val) => sum + val, 0) / errorPercentagesHigh.length
      : 0

  const avgErrorLow =
    errorPercentagesLow.length > 0
      ? errorPercentagesLow.reduce((sum, val) => sum + val, 0) / errorPercentagesLow.length
      : 0

  // R-squared
  let rSquared = 0
  if (vehicleErrorData.length > 0) {
    const actualPrices = vehicleErrorData.map(v => v.actualPrice)
    const meanActualPrice = actualPrices.reduce((sum, p) => sum + p, 0) / actualPrices.length

    let ssRes = 0
    let ssTot = 0

    vehicleErrorData.forEach(({ actualPrice }, idx) => {
      const predictedPrice = primaryPredictions[idx]
      if (predictedPrice == null || predictedPrice <= 0) return
      ssRes += Math.pow(actualPrice - predictedPrice, 2)
      ssTot += Math.pow(actualPrice - meanActualPrice, 2)
    })

    rSquared = ssTot > 0 ? Math.max(0, Math.min(1, 1 - ssRes / ssTot)) : 0
  }

  // Optimization uses formula-specific rules
  const effectiveRules = (formula.rules as FormulaRule[] | undefined) ?? []
  const optimizationResult = calculateOptimalChanges(
    formula,
    effectiveRules,
    vehicleErrorData,
    avgErrorPercent,
    priceType
  )

  // -----------------------------------------------------------------------
  // Holdout MAPE for formula (last 20%, out-of-sample estimate).
  // For small datasets (<5 total sales) where a 20% holdout would yield
  // fewer than 1 valid observation, fall back to all available sales.
  // -----------------------------------------------------------------------
  let holdoutMape: number | null = null

  const mapeSourceVehicles = holdoutSales.length >= 1
    ? holdoutSales
    : filteredSales // fallback: use all sales if holdout is empty

  const mapeVehicles = mapeSourceVehicles.filter(v => {
    const actualPrice = priceType === 'retail' ? v.optic_list_price : v.seller_asking_price
    return !!v.year && !!v.miles && !!actualPrice && actualPrice > 0
  })

  if (mapeVehicles.length >= 1) {
    const mapeInputs: PredictInput[] = mapeVehicles.map(v => ({
      year: v.year!,
      mileage: v.miles!,
      make: formula.vehicleMake,
      model: formula.vehicleModel,
      type: formula.vehicleType,
      size: formula.vehicleSize,
      fuel: formula.vehicleFuel,
      color: v.color || 'White',
      dateAdded: v.date_added || undefined,
    }))

    const mapePreds = await primaryPredictFn(mapeInputs)
    const mapeErrors: number[] = []
    mapeVehicles.forEach((v, idx) => {
      const actualPrice = priceType === 'retail' ? v.optic_list_price : v.seller_asking_price
      if (!actualPrice || actualPrice <= 0) return
      const predicted = mapePreds[idx]
      if (predicted == null || predicted <= 0) return
      mapeErrors.push(Math.abs((actualPrice - predicted) / actualPrice) * 100)
    })
    if (mapeErrors.length >= 1) {
      holdoutMape = mapeErrors.reduce((s, v) => s + v, 0) / mapeErrors.length
    }
  }

  // -----------------------------------------------------------------------
  // Automated MAPE — evaluated on ALL historical vehicles.
  // The WLS model is a broad model (not trained specifically for this formula
  // category), so evaluating it on all data gives the most reliable estimate
  // of how well it prices vehicles in this category.
  // -----------------------------------------------------------------------
  let automatedMape: number | null = null

  if (comparisonPredictFn) {
    // Build inputs directly from historicalSales (preserves color/dateAdded)
    const olsVehicles = historicalSales.filter(v => {
      const actualPrice = priceType === 'retail' ? v.optic_list_price : v.seller_asking_price
      return !!v.year && !!v.miles && !!actualPrice && actualPrice > 0
    })

    if (olsVehicles.length >= 1) {
      const olsInputs: PredictInput[] = olsVehicles.map(v => ({
        year: v.year!,
        mileage: v.miles!,
        make: formula.vehicleMake,
        model: formula.vehicleModel,
        type: formula.vehicleType,
        size: formula.vehicleSize,
        fuel: formula.vehicleFuel,
        color: v.color || 'White',
        dateAdded: v.date_added || undefined,
      }))

      const comparisonPreds = await comparisonPredictFn(olsInputs)
      const comparisonErrors: number[] = []
      olsVehicles.forEach((v, idx) => {
        const actualPrice = priceType === 'retail' ? v.optic_list_price : v.seller_asking_price
        if (!actualPrice || actualPrice <= 0) return
        const predicted = comparisonPreds[idx]
        if (predicted == null || predicted <= 0) return
        comparisonErrors.push(Math.abs((actualPrice - predicted) / actualPrice) * 100)
      })
      if (comparisonErrors.length >= 1) {
        automatedMape = comparisonErrors.reduce((s, v) => s + v, 0) / comparisonErrors.length
      }
    }
  }

  return {
    formula,
    sold,
    soldElsewhere,
    totalSold,
    avgErrorPercent,
    avgErrorHigh,
    avgErrorLow,
    countAbovePrediction,
    countBelowPrediction,
    totalAbovePrediction,
    totalBelowPrediction,
    rSquared,
    holdoutMape,
    automatedMape,
    holdoutVehicleIds: mapeSourceVehicles.map(v => v.id).filter((id): id is string => !!id),
    matchingVehicles: matchingVehicles.length,
    vehicleDescription,
    sampleSize: errorPercentages.length,
    optimizationResult,
    hasImprovement: !!optimizationResult && optimizationResult.improvement >= 1,
  }
}

// Hook to get a single formula's stats with React Query caching
export const useFormulaStat = (
  formula: Formula | null,
  rules?: FormulaRule[],
  priceType: 'retail' | 'wholesale' = 'retail',
  olsModel?: OLSTrainedModel | null,
  excludedVehicleIds: string[] = [],
  dateRange?: { start: Date | null; end: Date | null },
) => {
  const { vehicles, isLoading: isLoadingVehicles } = useInventory('master_inventory')
  const { predictPricesBatch } = useOLSPrediction()

  // Stable key from rules to trigger re-calculation when rules change.
  // Prefer embedded formula.rules (from useFormulasWithRules) over the passed param.
  const rulesKey = useMemo(() => {
    const effectiveRules = (formula?.rules as FormulaRule[] | undefined) ?? rules
    if (!effectiveRules || effectiveRules.length === 0) return 'no-rules'
    return effectiveRules
      .map(r => `${r.id}-${r.targetFactor}-${r.operationValue}-${r.lowerBound}-${r.upperBound}-${r.status}`)
      .join('|')
  }, [formula?.rules, rules])

  const olsModelKey = olsModel ? `ols-${olsModel.intercept}` : 'no-ols'
  const excludedIdsKey = excludedVehicleIds.join(',')
  const dateRangeKey = dateRange
    ? `${dateRange.start?.toISOString() ?? 'null'}-${dateRange.end?.toISOString() ?? 'null'}`
    : 'all'

  const { data: formulaStat, isLoading: isLoadingQuery } = useQuery({
    queryKey: ['formula-stat', formula?.id || 'null', priceType, rulesKey, olsModelKey, excludedIdsKey, dateRangeKey],
    queryFn: async () => {
      if (!vehicles) throw new Error('Vehicles not loaded')
      if (!formula) throw new Error('Formula not loaded')

      const effectiveRules = (formula.rules as FormulaRule[] | undefined) ?? rules ?? []
      const primaryPredictFn = createFormulaPredictFn(formula, effectiveRules, priceType)

      // If an OLS model is explicitly provided, use client-side coefficient prediction.
      // Otherwise fall back to the batch API (uses the currently active backend model).
      const comparisonPredictFn: PricingPredictFn | undefined = olsModel
        ? createOLSModelPredictFn(olsModel)
        : predictPricesBatch
          ? async (inputs) => {
              const preds = await predictPricesBatch(
                inputs.map(v => ({
                  year: v.year,
                  mileage: v.mileage,
                  make: v.make ?? '',
                  model: v.model ?? '',
                  type: v.type ?? '',
                  size: v.size ?? '',
                  fuel: (v.fuel === 'Diesel' ? 'Diesel' : 'Gas') as 'Diesel' | 'Gas',
                  color: v.color,
                  dateAdded: v.dateAdded,
                }))
              )
              return preds.map(p => {
                if (p.usedFallback) return null
                const price = priceType === 'retail' ? p.sellPrice : p.buyInPrice
                return price != null && price > 0 ? price : null
              })
            }
          : undefined

      return calculateSingleFormulaStat(formula, vehicles, priceType, primaryPredictFn, comparisonPredictFn, excludedVehicleIds, dateRange)
    },
    enabled: !!vehicles && !!formula,
    staleTime: 1000 * 60 * 5,
  })

  return {
    formulaStat,
    isLoading: isLoadingVehicles || isLoadingQuery,
  }
}

// Multi-formula stats hook using React Query
export const useFormulaStats = (
  formulas: Formula[],
  rules?: any[],
  priceType: 'retail' | 'wholesale' = 'retail',
  olsModel?: OLSTrainedModel | null,
  excludedVehicleIds: string[] = [],
  dateRange?: { start: Date | null; end: Date | null },
) => {
  const { vehicles, isLoading: isLoadingVehicles } = useInventory('master_inventory')
  const { predictPricesBatch } = useOLSPrediction()

  const formulaIds = useMemo(() => formulas.map(f => f.id).join(','), [formulas])
  const olsModelKey = olsModel ? `ols-${olsModel.intercept}` : 'no-ols'
  const excludedIdsKey = excludedVehicleIds.join(',')
  const dateRangeKey = dateRange
    ? `${dateRange.start?.toISOString() ?? 'null'}-${dateRange.end?.toISOString() ?? 'null'}`
    : 'all'

  const { data: formulaStats, isLoading: isLoadingQuery } = useQuery({
    queryKey: ['formula-stats-multi', formulaIds, priceType, olsModelKey, excludedIdsKey, dateRangeKey],
    queryFn: async () => {
      if (!vehicles) throw new Error('Vehicles not loaded')

      // Build the OLS comparison predict function once for all formulas.
      // Use client-side coefficient prediction if an OLS model is provided.
      const comparisonPredictFn: PricingPredictFn | undefined = olsModel
        ? createOLSModelPredictFn(olsModel)
        : predictPricesBatch
          ? async (inputs) => {
              const preds = await predictPricesBatch(
                inputs.map(v => ({
                  year: v.year,
                  mileage: v.mileage,
                  make: v.make ?? '',
                  model: v.model ?? '',
                  type: v.type ?? '',
                  size: v.size ?? '',
                  fuel: (v.fuel === 'Diesel' ? 'Diesel' : 'Gas') as 'Diesel' | 'Gas',
                  color: v.color,
                  dateAdded: v.dateAdded,
                }))
              )
              return preds.map(p => {
                if (p.usedFallback) return null
                const price = priceType === 'retail' ? p.sellPrice : p.buyInPrice
                return price != null && price > 0 ? price : null
              })
            }
          : undefined

      return Promise.all(formulas.map(formula => {
        const effectiveRules = (formula.rules as FormulaRule[] | undefined) ?? rules ?? []
        const formulaPredictFn = createFormulaPredictFn(formula, effectiveRules, priceType)
        return calculateSingleFormulaStat(formula, vehicles, priceType, formulaPredictFn, comparisonPredictFn, excludedVehicleIds, dateRange)
      }))
    },
    enabled: !!vehicles && formulas.length > 0,
    staleTime: 1000 * 60 * 5,
  })

  return {
    formulaStats: formulaStats || [],
    isLoading: isLoadingVehicles || isLoadingQuery,
  }
}

// Helper function to invalidate all formula stats in the cache
export const invalidateAllFormulaStats = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['formula-stat'] })
  queryClient.invalidateQueries({ queryKey: ['formula-stats-multi'] })
}

// Helper function to invalidate a specific formula's stats
export const invalidateFormulaStat = (
  queryClient: ReturnType<typeof useQueryClient>,
  formulaId: string
) => {
  queryClient.invalidateQueries({
    queryKey: ['formula-stat', formulaId]
  })
  queryClient.invalidateQueries({ queryKey: ['gray_book_formulas'] })
}
